import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Card, Space, Button, Divider, Modal, Form, Input, Checkbox, message } from 'antd';
import { ArrowLeftOutlined, MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './DataCoveragePage.css';

const { Title, Text, Paragraph } = Typography;

const DataCoveragePage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pageLoadTime] = useState(Date.now());
  
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem('waitlist_session_id');
    if (existing) return existing;
    
    const generateId = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
    };
    
    const newId = generateId();
    localStorage.setItem('waitlist_session_id', newId);
    return newId;
  });

  // Set page metadata
  useEffect(() => {
    document.title = 'Data Coverage | SNS Intelligence';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Data Coverage for SNS Intelligence - Comprehensive pharmaceutical market intelligence data coverage across therapeutic areas, pipeline development, market sizing, geographic regions, and company activity.');
  }, []);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (values: any) => {
    if (isSubmitting) return;
    
    message.success('You\'re on the list. We\'ll reach out when your access is approved.');
    setIsSubmitted(true);
    form.resetFields();
    setIsSubmitting(true);
    
    try {
      const hiddenFields = {
        referrer_url: document.referrer || undefined,
        landing_path: window.location.pathname,
        session_id: sessionId,
        honeypot: '',
        submit_timing: Date.now() - pageLoadTime
      };

      const response = await fetch('https://pharmadashv2-backend.onrender.com/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          marketing_opt_in: values.marketing_opt_in,
          ...hiddenFields
        }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      const result = await response.json();
      
      if (result.status === 'accepted' || result.status === 'ok') {
        // Success - already shown message
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateCompanyEmail = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Please enter your email'));
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject(new Error('Please enter a valid email'));
    }
    
    // List of free email providers to reject
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
      'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'yandex.com',
      'gmx.com', 'zoho.com', 'msn.com', 'inbox.com', 'mail.ru'
    ];
    
    const emailDomain = value.toLowerCase().split('@')[1];
    
    if (freeEmailProviders.includes(emailDomain)) {
      return Promise.reject(new Error('Please use your company email address'));
    }
    
    return Promise.resolve();
  };

  return (
    <div className="data-coverage-page">
      {/* Navigation */}
      <nav className="data-coverage-navbar">
        <div className="nav-container">
          <div className="nav-left">
            <div
              className="nav-logo"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  navigate('/');
                }
              }}
            >
              <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
            </div>
            <div className="nav-links">
              <a href="/platform">Platform</a>
              <a href="/markets">Markets</a>
              <a href="/pulse">Pulse</a>
              <a href="/press">Press</a>
            </div>
          </div>
          <div className="nav-actions">
            <Button type="primary" onClick={() => navigate('/login')}>Open Portal</Button>
          </div>
        </div>
      </nav>

      {/* Back to Home Button */}
      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      <div className="data-coverage-container">
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <div className="data-coverage-header">
              <Title level={1} className="data-coverage-title">
                Data Coverage
              </Title>
            </div>

            <Card className="data-coverage-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Paragraph className="data-coverage-intro">
                  The platform integrates data from multiple domains to provide a comprehensive view of pharmaceutical markets, enabling analysis that spans therapeutic areas, geographic regions, development stages, and market dimensions.
                </Paragraph>

                <div>
                  <Title level={2} className="section-title">
                    Therapeutic and Market Coverage
                  </Title>
                  <Paragraph>
                    The platform provides coverage across multiple therapeutic areas and markets, enabling analysis from broad therapeutic categories down to specific sub-markets. This hierarchical structure supports both high-level strategic assessment and detailed market evaluation. Users can analyze markets at the therapeutic area level or drill down into sub-markets within those areas.
                  </Paragraph>
                  <Paragraph>
                    Coverage spans therapeutic areas including dermatology and other major pharmaceutical markets (coming soon). The platform's structure enables comparison across therapeutic areas while maintaining the granularity needed for sub-market analysis. This approach supports both broad market assessment and focused opportunity evaluation within specific therapeutic contexts.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Pipeline and Development Coverage
                  </Title>
                  <Paragraph>
                    Pipeline coverage includes drug candidates in development, their associated developers, therapeutic classifications, and development stages. The platform tracks pipeline activity across multiple therapeutic areas and development phases, providing visibility into the pharmaceutical development landscape.
                  </Paragraph>
                  <Paragraph>
                    Coverage emphasizes linkage between pipeline candidates, developers, and market data, enabling analysis of how pipeline activity relates to market opportunity and competitive dynamics. The platform's structure ensures comparability across therapeutic areas and development stages, supporting portfolio planning and competitive monitoring.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Market Size and Forecast Coverage
                  </Title>
                  <Paragraph>
                    The platform includes market sizing data and forward-looking forecasts across multiple time horizons. Market size data provides quantitative assessment of market opportunity, while forecasts support strategic planning and opportunity evaluation. Coverage spans historical market data and forward-looking projections.
                  </Paragraph>
                  <Paragraph>
                    Market data is segmented across multiple dimensions, including therapeutic categories, routes of administration, drug types, distribution channels, and drug classes. This segmentation enables analysis at various levels of granularity while maintaining consistency across dimensions. The platform's structure supports both aggregate market analysis and detailed sub-segment evaluation.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Geographic Coverage
                  </Title>
                  <Paragraph>
                    Coverage includes global, regional, and country-level market data, enabling analysis across geographic dimensions. The platform provides data at multiple geographic levels, supporting both broad regional assessment and country-specific evaluation. This structure enables users to evaluate geographic opportunities and prioritize markets based on quantitative data.
                  </Paragraph>
                  <Paragraph>
                    Geographic coverage is designed to maintain comparability across regions and countries, ensuring that market data can be meaningfully compared across geographies. The platform's standardized approach to geographic segmentation supports consistent analysis regardless of the specific markets being evaluated.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Company and Developer Coverage
                  </Title>
                  <Paragraph>
                    The platform includes coverage across major pharmaceutical and biotechnology companies, as well as emerging developers. Company data encompasses market participation, pipeline activity, and strategic positioning. This coverage supports competitive intelligence and partnership evaluation.
                  </Paragraph>
                  <Paragraph>
                    Developer coverage includes both established pharmaceutical companies and emerging biotechnology firms, providing visibility into the full spectrum of pharmaceutical development activity. The platform's structure enables analysis of company activity across therapeutic areas and development stages, supporting competitive monitoring and strategic assessment.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Data Structure and Consistency
                  </Title>
                  <Paragraph>
                    The platform employs standardized definitions, harmonized segmentation, and consistent methodologies across all datasets. This approach ensures that data from different domains, therapeutic areas, or geographic regions can be meaningfully compared and analyzed together. Standardization supports professional decision-making by providing reliable, reproducible analytical frameworks.
                  </Paragraph>
                  <Paragraph>
                    Data structure is designed to support comparability across dimensions, enabling users to evaluate markets, pipeline activity, and competitive dynamics with confidence in data consistency. The platform's methodology ensures that analyses can be reproduced and validated, supporting use in professional and corporate decision-making contexts.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Updates and Maintenance
                  </Title>
                  <Paragraph>
                    Data coverage is maintained and updated over time to ensure relevance and continuity. Updates reflect changes in market dynamics, pipeline developments, and competitive landscapes. The platform's maintenance approach supports long-term strategic analysis by providing both current data and historical continuity.
                  </Paragraph>
                  <Paragraph>
                    Regular updates ensure that users have access to current market intelligence while maintaining the historical context needed for trend analysis and strategic planning. The platform's update process is designed to preserve data consistency and comparability across time periods.
                  </Paragraph>
                </div>

                <div>
                  <Title level={2} className="section-title">
                    Request Access
                  </Title>
                  <Paragraph>
                    SNS Intelligence is a subscription-based platform with controlled access, intended for professional and corporate use. Access is managed through organizational subscriptions, ensuring appropriate use and data security. If you are interested in learning more about our data coverage or discussing your organization's specific coverage needs, we invite you to request access or contact our team.
                  </Paragraph>
                  <div style={{ marginTop: '24px' }}>
                    <Space size="middle">
                      <Button 
                        type="primary" 
                        size="large"
                        onClick={showModal}
                      >
                        Request Free Trial
                      </Button>
                      <Button 
                        size="large"
                        onClick={() => {
                          navigate('/');
                          // Scroll to pricing section after navigation
                          setTimeout(() => {
                            const pricingSection = document.getElementById('pricing');
                            if (pricingSection) {
                              pricingSection.scrollIntoView({ behavior: 'smooth' });
                            } else {
                              // Fallback: try using hash
                              window.location.hash = 'pricing';
                            }
                          }, 300);
                        }}
                      >
                        View Pricing
                      </Button>
                    </Space>
                  </div>
                  <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e8e8e8' }}>
                    <Text style={{ color: '#595959', fontSize: '16px' }}>
                      Have questions? Contact us at{' '}
                      <a 
                        href="mailto:info@snsintel.com" 
                        style={{ 
                          color: '#1890ff', 
                          textDecoration: 'none',
                          fontWeight: 500
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        info@snsintel.com
                      </a>
                    </Text>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <footer className="data-coverage-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Trial Request Modal */}
      <Modal
        title="Request Free Trial"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={500}
      >
        {!isSubmitted ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              name="email"
              label="Company Email"
              rules={[{ validator: validateCompanyEmail }]}
            >
              <Input 
                placeholder="your.email@company.com" 
                size="large"
                prefix={<MailOutlined />}
              />
            </Form.Item>

            <Form.Item
              name="marketing_opt_in"
              valuePropName="checked"
              rules={[{ 
                validator: (_, value) => 
                  value ? Promise.resolve() : Promise.reject(new Error('Please accept to continue'))
              }]}
            >
              <Checkbox>
                I agree to receive updates about the trial and marketing communications.
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                size="large"
                loading={isSubmitting}
                block
                htmlType="submit"
              >
                Request Free Trial
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={4}>You're on the list!</Title>
            <Paragraph>
              We'll reach out when your access is approved.
            </Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DataCoveragePage;
